<template lang='pug'>
.facility-slide-out(:class='{ "has-facility" : asset !== null }')
	asset-slide-out(
		:state='state'
		:asset='asset'
		:asset-key='assetKey'
		:asset-name='assetName'
		@close='$emit( "close" )'
		@edit='$emit( "edit" )'
		@delete='$emit( "delete" )'
	)

		.title-container(
			v-if='asset'
			slot='title'
		)
			h1.title Facility

		page.facilities-page.side-sheet-content(
			v-if='asset'
			slot='side-sheet-content'
			:tabs='tabs'
			@active-tab='setActiveTab'
		)

			.body-wrapper(slot='main-body')
				facility(
					v-if='Object.keys( asset ).length'
					:facility='asset'
				)

				.loading-indicator.spacey(v-else) Loading
					span .
					span .
					span .

			.body.comments(slot='tab-comments')
				comments(
					:section-id='`facilities-${asset.facilitiesKey}`'
					:user='user'
					@replied-to-comment='email'
				)

			.body.jobs(slot='tab-jobs')

				.loading-indicator.spacey(v-if='tabContent.jobs.state === "loading"') Loading
						span .
						span .
						span .

				.no-results(v-if='tabContent.jobs.state === "loaded" && tabContent.jobs.data.length === 0')
					p.spacey No Results Shown

				tab-box-content(
					v-else-if='tabContent.jobs.state === "loaded"'
					v-for='job in tabContent.jobs.data'
					:key='job.number'
					@click.native='viewAsset( job, "jobs" )'
				)

					.top(slot='top')
						p.title \#{{ job.number }}
						p.sub-title {{ job.name }}

					.bottom(slot='bottom')

						.status
							.bubble(:class='{ active : job.status === "Active" }')
							p {{ job.status }}

						.manage
							.icon-person-outlined
							p Managed by {{ job.manager }}

						.funded
							.icon-money
							p Funded by {{ job.fundedBy }}

			.body.agreements(slot='tab-agreements')

				.loading-indicator.spacey(v-if='tabContent.agreements.state === "loading"') Loading
					span .
					span .
					span .

				.no-results(v-if='tabContent.agreements.state === "loaded" && tabContent.agreements.data.length === 0')
					p.spacey No Results Shown

				tab-box-content(
					v-else-if='tabContent.agreements.state === "loaded" '
					v-for='agreement in tabContent.agreements.data'
					:key='agreement.recordNumber'
					@click.native='viewAsset( agreement, "agreements" )'
				)

					.top(slot='top')
						p.title \#{{ agreement.recordNumber }}
						p.sub-title {{ agreement.recordOwner }}

					.bottom(slot='bottom')

						.status
							p {{ agreement.type }}


			.body.permits(slot='tab-permits')

				.loading-indicator.spacey(v-if='tabContent.permits.state === "loading"') Loading
						span .
						span .
						span .

				.no-results(v-if='tabContent.permits.state === "loaded" && tabContent.permits.data.length === 0')
					p.spacey No Results Shown

				tab-box-content(
					v-else-if='tabContent.permits.state === "loaded" '
					v-for='permit in tabContent.permits.data'
					:key='permit.number'
					@click.native='viewAsset( permit, "permits" )'
				)

					.top(slot='top')
						p.title \#{{ permit.number }}
						p.sub-title {{ permit.name }}

					.bottom(slot='bottom')

						.fid-number
							p Const. -
								span  FID\#{{ permit.constNumber }}
</template>

<script>
import Facility from '@/components/slide-outs/facilities/Facility';
import AssetSlideOut from '@/components/slide-outs/AssetSlideOut';
import Comments from '@/components/comments';
import Page from '@/components/Page';
import TabBoxContent from '@/components/TabBoxContent';

import { objectOrNull } from '@/lib/vue-validators';
import {
	GetFacilityJobs,
	GetFacilityAgreements,
	GetFacilityPermits,
	SendEmail
} from '@/lib/API';


export default {
	name : 'facility-slide-out',

	props : {
		state : {
			type     : String,
			required : true,
		},

		asset : {
			required  : true,
			validator : objectOrNull,
		},

		assetKey : {
			required : true,
		},

		assetName : {
			type : String,
		}
	},

	data : () => ( {
		tabs : {
			'comments'   : 'Comments',
			'jobs'       : 'Jobs',
			'agreements' : 'Agreements',
			'permits'    : 'Permits',
		},

		activeTab : 'comments',

		tabContent : {

			comments : {
				state : 'loaded',
			},

			jobs : {
				state : '',
				data  : null,
			},

			agreements : {
				state : '',
				data  : null,
			},

			permits : {
				state : '',
				data  : null,
			},
		}
	} ),

	computed : {

		user() {
			return this.$store.state.postgresUser;
		},

	},

	watch : {

		activeTab : {
			immediate : false,
			handler( val ) {

				// the reason why I'm just grabbing the object and storing
				// it as a variable instead of looping through a "loadedTabs"
				// array and seeing if the value is already in there, is
				// because this is faster due to the fact that this is only going
				// to ever compute at a rate of O(1) vs O(n)
				const tab = this.tabContent[val];

				if ( tab.state !== 'loaded' ) {

					tab.state = 'loading';

					const Val = val.replace( /^.{1}/, a => a.toUpperCase() );

					Promise.resolve()
						.then( () => this[`load${Val}`]() )
						.then( ( value ) => {
							tab.state = 'loaded';
							tab.data  = value;
						} )
						.catch( ( e ) => {
							tab.state  = 'failed';
							tab.errror = e.message;
						} );

				}

			}
		},

		assetKey( key ) {
			const tabs = ['jobs', 'agreements', 'permits'];

			if ( key === null ) {
				tabs.forEach( ( tab ) => {

					this.tabContent[tab] = {
						state : '',
						data  : null,
					};

				} );

				this.activeTab = 'comments';
			}
		},

	},

	methods : {

		setActiveTab( tab ) {
			this.activeTab = tab;
		},

		viewAsset( asset, assetType ) {

			this.$router.push( {
				name  : assetType,
				query : {
					key : asset.key,
				}
			} );
		},

		loadJobs() {
			return GetFacilityJobs( this.asset.facilitiesKey )
				.then( jobs => jobs.map( job => ( {
					key : job.jobsKey,
					...job,
				} ) ) );
		},

		loadAgreements() {
			return GetFacilityAgreements( this.asset.facilitiesKey )
				.then( agreements => agreements.map( agreement => ( {
					key : agreement.agreementsKey,
					...agreement,
				} ) ) );
		},

		loadPermits() {
			return GetFacilityPermits( this.asset.facilitiesKey )
				.then( permits => permits.map( permit => ( {
					key : permit.permitsKey,
					...permit,
				} ) ) );
		},


		email( recipientKey, link ) {
			const { asset } = this;

			const sectionDescription = `Facility ${asset.name} No. ${asset.number}`;

			const emailData = {
				sectionDescription,
				link
			};

			SendEmail( this.user.usersKey, [recipientKey], 'reply', emailData );
		}

	},

	components : {
		Facility,
		AssetSlideOut,
		Comments,
		Page,
		TabBoxContent,
	}
};
</script>

<style lang='scss'>
.facility-slide-out {

	&:not(.has-facility) {

		.toggle,
		.main {
			opacity: 0;
		}
	}

	.side-sheet .swipers .swiper-wrapper .swiper-slide:not(:nth-child(1)) {
		width: 700px;
		background: white;
	}

	.body.comments {

		.comment-section {

			.comment-count {
				padding: 15px 30px;
			}

			.comment-thread-container {
				padding: 0 15px;

				.comment-thread-container {
					padding: 0;
				}
			}

			.comment-wrapper {
				margin: 15px 30px;
			}
		}
	}

	.body-wrapper {
		width: 100%;

		.loading-indicator {
			text-align: center;
		}
	}

	.main {

		.body {

			&.agreements, &.jobs, &.permits {
				position: relative;
				padding-top: 15px;
				z-index: 1;

				&::before {
					content: '';
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 80px;
					background: linear-gradient(180deg, #F3F6FA 0%, rgba(243, 246, 250, 0) 100%);
					z-index: -1;
				}

				.loading-indicator {
					text-align: center;
				}
			}

			.no-results {

				p {
					text-align: center;
				}
			}

			.tab-box-content {
				margin: 15px;
				padding: 15px;
				position: relative;
				z-index: 1;
				cursor: pointer;

				.bottom {

					.manage, .funded {
						overflow: hidden;
						// we get 40% because there's two other elements besides the status bubble
						// and the status bubble has a width of 20%, meaning 80%, meaning 80/n elements
						// = 40%. We get 40px because there's a margin-right of 20px on both elements.
						flex: 0 0 calc(40% - 40px);
					}

					.status, .manage, .funded {
						position: relative;
						display: flex;
						flex-flow: row nowrap;
						align-items: center;
						color: $input-color;

						p {
							display: block;
							overflow: hidden;
							white-space: nowrap;
							text-overflow: ellipsis;
						}
					}

					.status {
						position: relative;
						display: flex;
						flex-flow: row nowrap;
						align-items: center;
						flex: 0 0 20%;

						.bubble {
							position: relative;
							width: 10px;
							height: 10px;
							margin-right: 14px;

							&::before {
								content: '';
								width: 10px;
								height: 10px;
								border: 4px solid darken( #c4c4c4, 10% );
								border-radius: 50%;
								background: #c4c4c4;
								position: absolute;
								top: 50%;
								transform: translateY( -50% );
							}

							&.active {

								&::before {
									border-color: $color-primary;
									background: rgba( $color-primary, .5 );
								}

								&+p {
									color: $color-primary;
								}
							}
						}
					}

					.date-range {

						p {
							color: $color-primary;
						}
					}

					.fid-number {
						margin-top: 15px;

						p {
							color: $color-primary;
							font-weight: 600;

							span {
								font-weight: 300;
								color: inherit;
							}
						}
					}
				}
			}

			> p.spacey {
				text-align: center;
			}
		}
	}
}
</style>
